import { Pagination, UnknownObject } from '@shared/types';
import { MRT_Row, MRT_RowData, MRT_TableInstance } from 'material-react-table';
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useCallback, useEffect, useRef } from 'react';

interface UseMultiRowSelectionProps {
    rowSelection: UnknownObject;
    setRowSelection: Dispatch<SetStateAction<UnknownObject>>;
    setPagination: Dispatch<SetStateAction<Pagination>>;
    isLoading?: boolean;
    enableRowClickSelection?: boolean;
}

const useMultiRowSelection = <T extends MRT_RowData>({
    rowSelection,
    setRowSelection,
    setPagination,
    isLoading,
    enableRowClickSelection,
}: UseMultiRowSelectionProps) => {
    const lastClickedIndex = useRef<number | null>(null);

    // Сбрасываем lastClickedIndex когда выбор стал пустым
    useEffect(() => {
        if (Object.keys(rowSelection).length === 0) {
            lastClickedIndex.current = null;
        }
    }, [rowSelection]);

    const handleRowClick = useCallback(
        (e: MouseEvent, row: MRT_Row<T>, table: MRT_TableInstance<T>) => {
            const allRows = table.getRowModel().rows;
            const currentIndex = allRows.findIndex((r) => r.id === row.id);

            if (e.shiftKey && lastClickedIndex.current !== null) {
                const from = Math.min(lastClickedIndex.current, currentIndex);
                const to = Math.max(lastClickedIndex.current, currentIndex);

                const newSelection: UnknownObject = { ...rowSelection };
                for (let i = from; i <= to; i++) {
                    newSelection[allRows[i].id] = true;
                }
                setRowSelection(newSelection);
            } else {
                row.toggleSelected();
                lastClickedIndex.current = currentIndex;
            }

            if (!e.shiftKey) {
                lastClickedIndex.current = currentIndex;
            }
        },
        [rowSelection, setRowSelection],
    );

    const handleSelectAllChange = useCallback(
        (_e: ChangeEvent<HTMLInputElement>, table: MRT_TableInstance<T>) => {
            lastClickedIndex.current = null;

            const allRows = table.getRowModel().rows;
            const selectedCount = allRows.filter((row) => rowSelection[row.id]).length;
            const isAllSelected = selectedCount === allRows.length;

            if (isAllSelected) {
                // Снимаем выбор только с текущей страницы
                const newSelection = { ...rowSelection };
                allRows.forEach((row) => {
                    delete newSelection[row.id];
                });
                setRowSelection(newSelection);
            } else {
                // Выбираем все строки текущей страницы
                const newSelection = { ...rowSelection };
                allRows.forEach((row) => {
                    newSelection[row.id] = true;
                });
                setRowSelection(newSelection);
            }
        },
        [rowSelection, setRowSelection],
    );

    const handlePaginationChange = (updater: any) => {
        setPagination(updater);
        lastClickedIndex.current = null;
    };
    return {
        handlePaginationChange,
        handleSelectAllChange,
        handleRowClick,
        tableProps: {
            // Перехватываем клик на чекбокс в шапке
            muiSelectAllCheckboxProps: ({ table }: { table: MRT_TableInstance<T> }) => {
                const allRows = table.getRowModel().rows;
                const selectedOnPage = allRows.filter((row) => rowSelection[row.id]).length;
                const totalOnPage = allRows.length;
                const totalSelected = Object.keys(rowSelection).length;

                const isAllPageSelected = totalOnPage > 0 && selectedOnPage === totalOnPage;
                const isIndeterminate = totalSelected > 0 && !isAllPageSelected;
                const isChecked = isAllPageSelected;

                return {
                    checked: isChecked,
                    indeterminate: isIndeterminate,
                    onChange: (e: ChangeEvent<HTMLInputElement>) => handleSelectAllChange(e, table),
                };
            },
            enableRowSelection: true,
            enableMultiRowSelection: true,
            enableBatchRowSelection: true,
            onRowSelectionChange: setRowSelection,
            ...(enableRowClickSelection
                ? {
                      muiTableBodyRowProps: ({
                          row,
                          table,
                      }: {
                          row: MRT_Row<T>;
                          staticRowIndex: number;
                          table: MRT_TableInstance<T>;
                      }) => {
                          return {
                              onClick: (e: React.MouseEvent) => handleRowClick(e, row, table),
                              onMouseDown: (e: MouseEvent<HTMLTableRowElement>) => {
                                  if (e.shiftKey) e.preventDefault();
                              },
                              selected: Boolean(rowSelection[row.id]),
                          };
                      },
                  }
                : {}),

            displayColumnDefOptions: {
                'mrt-row-select': {
                    size: 28,
                    minSize: 28,
                    maxSize: 28,
                    muiTableHeadCellProps: {
                        sx: {
                            p: 0,
                            pt: '2px',
                        },
                    },
                    muiTableBodyCellProps: {
                        sx: {
                            p: 0,
                            pt: '4px',
                            ...(isLoading ? { pl: '6px', pt: 1 } : undefined),
                            display: 'flex',
                            alignItems: 'flex-start',
                        },
                    },
                },
                muiTableBodyCellProps: {
                    sx: {
                        p: 0,
                    },
                },
            },
        },
    };
};

export default useMultiRowSelection;
